import { useMemo, useState, useCallback, useEffect } from "react";
import TourOverlay from "../activation/TourOverlay";
import { useTourLayout } from "../activation/useTourLayout";
import { useOverlayOpen } from "../activation/useOverlayOpen";
import {
  getTourActionHint,
  isManualTourSubstep,
  isTourAckSubstep,
} from "../activation/activationPath";
import { validateTourSubstep } from "../activation/tourValidation";
import { useTrainingMode } from "./TrainingModeContext";
import { getTrainingLessonSubsteps, getTrainingNavOpts } from "./trainingGuide";
import { useIsMobile } from "../hooks/useMediaQuery";

export default function TrainingTour() {
  const {
    active,
    track,
    currentLesson,
    trainingSubstepId,
    completed,
    skipped,
    totalLessons,
    advanceTrainingSubstep,
    completeLesson,
    markTourVisit,
    skipLesson,
    setCoachCollapsed,
    exitTraining,
  } = useTrainingMode();
  const isMobile = useIsMobile(768);
  const overlayOpen = useOverlayOpen();

  const navOpts = useMemo(
    () => getTrainingNavOpts(isMobile, track),
    [isMobile, track],
  );

  const substeps = useMemo(
    () =>
      currentLesson
        ? getTrainingLessonSubsteps(currentLesson.id, navOpts)
        : [],
    [currentLesson, navOpts],
  );

  const currentSubstep = useMemo(
    () => substeps.find((x) => x.id === trainingSubstepId) || null,
    [substeps, trainingSubstepId],
  );

  const selector = currentSubstep?.target || null;
  const lessonDone =
    currentLesson &&
    (completed.includes(currentLesson.id) ||
      skipped.includes(currentLesson.id));

  const subIdx = useMemo(() => {
    if (!substeps.length || !trainingSubstepId) return 0;
    return Math.max(0, substeps.findIndex((x) => x.id === trainingSubstepId));
  }, [substeps, trainingSubstepId]);

  const isLastSub = substeps.length > 0 && subIdx >= substeps.length - 1;
  const isNavStep = currentSubstep?.kind === "nav";
  const tourAck = isTourAckSubstep(trainingSubstepId);
  const manualAdvance =
    isManualTourSubstep(trainingSubstepId) || tourAck;
  const [advanceBlock, setAdvanceBlock] = useState("");

  useEffect(() => {
    setAdvanceBlock("");
  }, [trainingSubstepId]);

  const handleAdvance = useCallback(() => {
    if (!currentLesson?.id || !trainingSubstepId) return;
    const check = validateTourSubstep(currentLesson.id, trainingSubstepId);
    if (!check.ok) {
      setAdvanceBlock(check.message || "Заполните поле, чтобы продолжить");
      return;
    }
    setAdvanceBlock("");

    if (isLastSub && tourAck) {
      markTourVisit(currentLesson.id);
      completeLesson(currentLesson.id);
      return;
    }

    advanceTrainingSubstep();
  }, [
    advanceTrainingSubstep,
    completeLesson,
    currentLesson?.id,
    isLastSub,
    markTourVisit,
    tourAck,
    trainingSubstepId,
  ]);

  const handleSkipLesson = useCallback(() => {
    if (!currentLesson?.id) return;
    skipLesson(currentLesson.id);
  }, [currentLesson?.id, skipLesson]);

  const layout = useTourLayout(
    active && !lessonDone && Boolean(currentLesson && currentSubstep),
    selector,
    [trainingSubstepId, overlayOpen, manualAdvance],
    { compact: !manualAdvance },
  );

  const contentFallback =
    !isNavStep && Boolean(currentSubstep) && !layout?.callout && !layout?.hole;

  if (!active || !currentLesson || !currentSubstep || lessonDone) return null;

  return (
    <TourOverlay
      visible
      layout={layout}
      title={currentSubstep.title}
      hint={
        contentFallback
          ? `${currentSubstep.hint} Откройте нужный раздел или вкладку на экране.`
          : currentSubstep.hint
      }
      isMobile={isMobile}
      isNavStep={isNavStep}
      contentFallback={contentFallback}
      isLastSub={isLastSub}
      isTourAck={tourAck}
      stepIndex={currentLesson.index}
      stepTotal={totalLessons}
      hideLabel="Выйти из обучения"
      overlayOpen={overlayOpen}
      advanceLabel={tourAck ? "Понятно, дальше" : "Далее"}
      actionHint={getTourActionHint(trainingSubstepId)}
      onHide={() => exitTraining()}
      onAdvance={handleAdvance}
      onSkipLesson={handleSkipLesson}
      onOpenMenu={() => setCoachCollapsed(false)}
      manualAdvance={manualAdvance}
      advanceBlock={advanceBlock}
      subIndex={subIdx + 1}
      subTotal={substeps.length}
      kicker={track === "quick" ? "Быстрый старт" : "Обучение"}
      arrowId="trnTourArrowHead"
    />
  );
}
